import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SITE } from "@/lib/site";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Docs from "@/pages/Docs";

const Loader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="font-mono-tech text-neon-turquoise text-sm animate-pulse-glow tracking-widest">LOADING FUTRA OS...</div>
  </div>
);

const Protected = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
};

// Production subdomains (app./docs./admin.futra.pro) get scoped routers.
// Preview (preview.emergentagent.com) & localhost fall back to full path routing.
function AppRoutes() {
  if (SITE.isFutra && SITE.isDocsHost) {
    return <Routes><Route path="*" element={<Docs />} /></Routes>;
  }

  if (SITE.isFutra && SITE.isAdminHost) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Protected admin><Admin /></Protected>} />
      </Routes>
    );
  }

  if (SITE.isFutra && SITE.isAppHost) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/app/*" element={<Protected><Dashboard /></Protected>} />
        <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    );
  }

  if (SITE.isFutra) {
    // Marketing host: futra.pro — landing only, everything else bounces home.
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Development / preview: single host, full path-based routing.
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/app/*" element={<Protected><Dashboard /></Protected>} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0d0d0f", border: "1px solid #222", color: "#f5f5f5", borderRadius: 0, fontFamily: "JetBrains Mono, monospace" } }} />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
